import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GameCalendar({ user }) {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [allGames, setAllGames] = useState([]); // Store all games
  const [teams, setTeams] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month"); // month or week
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameDetails, setShowGameDetails] = useState(false);
  const [myChildrenTeams, setMyChildrenTeams] = useState([]); // Academy parent's children's teams

  // Determine user type
  const isAcademyParent = user?.roles?.includes("academy_parent");
  const isSectionMember = user?.member_type === "section" || user?.role === "captain" || user?.role === "convenor";

  useEffect(() => {
    fetchGames();
    fetchTeams();
    if (isAcademyParent) {
      fetchMyChildrenTeams();
    }
  }, []);

  // Re-filter games when children's teams are loaded
  useEffect(() => {
    if (allGames.length > 0 && myChildrenTeams.length > 0) {
      const filteredGames = filterGamesByRole(allGames);
      setGames(filteredGames);
    }
  }, [myChildrenTeams]);

  const fetchMyChildrenTeams = async () => {
    try {
      // Get parent's children
      const response = await axios.get(`${API}/academy/my-children`, { withCredentials: true });
      const children = response.data;
      
      // Extract unique team IDs from children
      const teamIds = [...new Set(children.map(child => child.team_id).filter(Boolean))];
      setMyChildrenTeams(teamIds);
    } catch (error) {
      console.error("Error fetching children's teams:", error);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games`, { withCredentials: true });
      setAllGames(response.data);
      
      // Apply role-based filtering
      const filteredGames = filterGamesByRole(response.data);
      setGames(filteredGames);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const filterGamesByRole = (gamesToFilter) => {
    // Admin and convenors see all games
    if (user?.role === "convenor" || user?.role === "admin") {
      return gamesToFilter;
    }

    // Academy coaches (non-section): show only games for teams they coach
    const isAcademyCoach = user?.roles?.includes("academy_coach");
    const coachTeams = user?.academy_coach_teams || [];
    if (isAcademyCoach && !isSectionMember && coachTeams.length > 0) {
      return gamesToFilter.filter(game => coachTeams.includes(game.team_id));
    }

    // Academy-only parents: show only games for their children's teams
    if (isAcademyParent && !isSectionMember) {
      return gamesToFilter.filter(game => myChildrenTeams.includes(game.team_id));
    }

    // Section members: show section games (default behavior)
    // Multi-role users (section member + academy parent/coach): show all for now
    return gamesToFilter;
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const getTeamColor = (teamId) => {
    const colors = [
      "bg-blue-500/20 border-blue-500/30 text-blue-300",
      "bg-emerald-500/20 border-emerald-500/30 text-emerald-300",
      "bg-purple-500/20 border-purple-500/30 text-purple-300",
      "bg-pink-500/20 border-pink-500/30 text-pink-300",
      "bg-yellow-500/20 border-yellow-500/30 text-yellow-300",
      "bg-red-500/20 border-red-500/30 text-red-300",
      "bg-indigo-500/20 border-indigo-500/30 text-indigo-300",
      "bg-orange-500/20 border-orange-500/30 text-orange-300",
    ];
    const index = teams.findIndex(t => t.id === teamId);
    return colors[index % colors.length];
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || "Unknown Team";
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getGamesForDate = (date) => {
    if (!date) return [];
    
    return games.filter(game => {
      const gameDate = new Date(game.date);
      return (
        gameDate.getDate() === date.getDate() &&
        gameDate.getMonth() === date.getMonth() &&
        gameDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMonthYear = () => {
    return currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const openGameDetails = (game) => {
    setSelectedGame(game);
    setShowGameDetails(true);
  };

  const days = getDaysInMonth(currentDate);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-4">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-slate-400">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/games")}
              variant="ghost"
              className="text-yellow-500 hover:text-yellow-500"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to List View
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={goToToday} variant="outline" size="sm">
              Today
            </Button>
            <Button onClick={() => navigate("/games")} variant="outline" size="sm">
              <List className="w-4 h-4 mr-2" />
              List View
            </Button>
          </div>
        </div>

        {/* Calendar Card */}
        <Card className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <CalendarIcon className="w-6 h-6 text-yellow-500" />
                Game Calendar
              </h1>
              {isAcademyParent && !isSectionMember && (
                <p className="text-sm text-slate-400 mt-1">
                  Showing games for your children&apos;s teams
                </p>
              )}
              {user?.roles?.includes("academy_coach") && !isSectionMember && (
                <p className="text-sm text-slate-400 mt-1">
                  Showing games for teams you coach
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <Button onClick={goToPreviousMonth} variant="outline" size="sm">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-lg font-semibold text-white min-w-[200px] text-center">
                {formatMonthYear()}
              </span>
              <Button onClick={goToNextMonth} variant="outline" size="sm">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center font-semibold text-slate-400 text-sm py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((date, index) => {
              const dayGames = getGamesForDate(date);
              const today = isToday(date);
              
              return (
                <div
                  key={index}
                  className={`min-h-[120px] p-2 rounded-lg border-2 ${
                    date
                      ? today
                        ? "bg-yellow-500/10 border-yellow-500/30"
                        : "bg-slate-900/50 border-white/5 hover:border-white/5"
                      : "bg-[#0a0f1a] border-slate-600"
                  } transition-colors`}
                >
                  {date && (
                    <>
                      <div className={`text-sm font-semibold mb-2 ${today ? "text-yellow-500" : "text-slate-400"}`}>
                        {date.getDate()}
                      </div>
                      
                      <div className="space-y-1">
                        {dayGames.slice(0, 3).map(game => (
                          <div
                            key={game.id}
                            onClick={() => openGameDetails(game)}
                            className={`text-xs p-2 rounded border cursor-pointer hover:shadow-md transition-shadow ${getTeamColor(game.team_id)}`}
                          >
                            <div className="font-semibold truncate">
                              vs {game.opponent}
                            </div>
                            <div className="text-xs opacity-75">
                              {formatTime(game.date)}
                            </div>
                          </div>
                        ))}
                        
                        {dayGames.length > 3 && (
                          <div className="text-xs text-slate-400 text-center py-1">
                            +{dayGames.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Legend */}
        <Card className="mt-4 p-4 bg-slate-900/50 border border-white/5">
          <h3 className="font-semibold text-white mb-3">Teams</h3>
          <div className="flex flex-wrap gap-3">
            {teams.map(team => (
              <div key={team.id} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded border-2 ${getTeamColor(team.id)}`}></div>
                <span className="text-sm text-slate-300">{team.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Game Details Modal */}
      {showGameDetails && selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full p-6 bg-slate-900/50 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Game Details</h2>
              <button
                onClick={() => setShowGameDetails(false)}
                className="text-slate-400 hover:text-slate-400"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm text-slate-400">Team:</span>
                <p className="font-semibold text-white">{getTeamName(selectedGame.team_id)}</p>
              </div>
              
              <div>
                <span className="text-sm text-slate-400">Opponent:</span>
                <p className="font-semibold text-white">{selectedGame.opponent}</p>
              </div>
              
              <div>
                <span className="text-sm text-slate-400">Date & Time:</span>
                <p className="font-semibold text-white">
                  {new Date(selectedGame.date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                  {" at "}
                  {formatTime(selectedGame.date)}
                </p>
              </div>
              
              <div>
                <span className="text-sm text-slate-400">Location:</span>
                <p className="font-semibold text-white">{selectedGame.location}</p>
              </div>
              
              <div>
                <span className="text-sm text-slate-400">Squad:</span>
                <p className="font-semibold text-white">
                  {selectedGame.signups?.length || 0}
                  {selectedGame.squad_limit ? ` / ${selectedGame.squad_limit}` : ""} players
                </p>
              </div>
              
              {selectedGame.standby && selectedGame.standby.length > 0 && (
                <div>
                  <span className="text-sm text-slate-400">Standby:</span>
                  <p className="font-semibold text-white">
                    {selectedGame.standby.length} players
                  </p>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex gap-2">
              <Button
                onClick={() => navigate("/games")}
                className="flex-1 bg-yellow-500/100 hover:bg-yellow-400 text-black"
              >
                View Full Details
              </Button>
              <Button
                onClick={() => setShowGameDetails(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
